import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileCheck2 } from 'lucide-react';
import { api } from '../../lib/api.js';

export function EinvoiceSection() {
  const queryClient = useQueryClient();
  const statusQuery = useQuery({ queryKey: ['einvoice', 'status'], queryFn: () => api.get('/einvoice/status') });
  const applicabilityQuery = useQuery({
    queryKey: ['einvoice', 'applicability'],
    queryFn: () => api.get('/einvoice/applicability'),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ fy, required }) => api.put(`/einvoice/applicability/${fy}`, { required }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['einvoice', 'applicability'] }),
  });

  const status = statusQuery.data;

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">e-Invoicing (IRN)</h2>
        <p className="mt-1 text-sm text-muted">
          Once a financial year is marked required below, any sale with a customer GSTIN is registered with the IRP
          before it prints — the invoice itself is never held up waiting for the portal.
        </p>
      </div>

      {statusQuery.isLoading ? null : !status?.configured ? (
        <p className="rounded-card border border-warning/30 bg-warning/10 px-3.5 py-3 text-sm text-warning">
          Not set up yet — a GSP (GST Suvidha Provider) account is needed first. Fill in{' '}
          <code>EINVOICE_ENABLED</code>, <code>EINVOICE_BASE_URL</code> and the credential fields in{' '}
          <code>backend/.env</code>. Financial years can still be marked required below — invoices will queue and
          register automatically once credentials are in place.
        </p>
      ) : (
        <p className="flex items-center gap-1.5 rounded-card border border-success/30 bg-success/10 px-3.5 py-2.5 text-sm text-success">
          <FileCheck2 size={16} aria-hidden="true" /> IRP connection configured.
        </p>
      )}

      <div className="flex flex-col gap-2">
        {(applicabilityQuery.data ?? []).map((row) => (
          <label key={row.fy} className="flex items-center justify-between rounded-card border border-border px-3.5 py-2.5">
            <span className="text-sm font-medium">FY {row.fy}</span>
            <span className="flex items-center gap-2 text-sm text-muted">
              {row.required ? 'Required' : 'Not required'}
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={row.required}
                disabled={toggleMutation.isPending}
                onChange={(e) => toggleMutation.mutate({ fy: row.fy, required: e.target.checked })}
              />
            </span>
          </label>
        ))}
      </div>
    </section>
  );
}

export default EinvoiceSection;
